(ns frontend.view.profile.view
  (:require [clojure.string :as string]
            [common.features.plan :as plan]
            [common.localization :as loc]
            [common.schema.registry :as registry]
            [common.security.authentication :as authentication]
            [common.session-timeout :as session-timeout]
            [frontend.actions.account :as account-actions]
            [frontend.db :as app-db]
            [frontend.global-events.toast :as toast]
            [frontend.logging :as logging]
            [frontend.page-components.default-page-layout :as default-page-layout]
            [frontend.page-components.zap-toolbar :as zap-toolbar]
            [frontend.routing :as routing]
            [frontend.state.current-session :as session]
            [frontend.tenant-customizations :as tenant-customizations]
            [frontend.utils :as fu]
            [frontend.view.components.clipboard :as clipboard]
            [frontend.view.components.cropper :as cropper]
            [frontend.view.entity-viewers.activity.insider-activity :as insider-activity]
            [frontend.view.modals.change-password.view :as change-password]
            [frontend.view.modals.events :as modals]
            [frontend.view.profile.components.auth :as auth]
            [frontend.view.profile.components.sessions :as sessions]
            [frontend.view.profile.db :as db]
            [frontend.view.profile.events :as events]
            [frontend.view.settings2.connected-plugins.view :as connected-plugins]
            [oops.core :as oops]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [ui.components.button.view :as button]
            [ui.components.input.simple-text :as simple-text-input]
            [ui.components.link.view :as link]
            [ui.components.radio-group.view :as radio-group]
            [ui.components.radix-ui.avatar.view :as avatar]
            [ui.components.radix-ui.popover.view :as popover]
            [ui.components.radix-ui.tooltip.view :as tooltip]
            [ui.components.select.select :as select]
            [ui.components.toggle.view :as toggle]
            [ui.dom-globals :as dg]
            [ui.editors.labelled-input.view :as labelled-input]
            [ui.tailwind-theme :as tailwind-theme]))

(defn attribute-form
  [{:keys [attribute]}]
  (let [account (merge @db/account (:edit-personal @db/state))
        old-value (get account attribute)]
    [labelled-input/view
     {:label     (let [fs (registry/get-field-schema @session/tenant-schema
                                                     attribute)]
                   (loc/field-name attribute fs))
      :data-type :data.type/email
      :dom-id    (str "profile-view-" (name attribute))
      :value     old-value
      :on-change (fn [new-val]
                   (events/modify-my-info! attribute new-val))
      :on-blur   (fn [new-value]
                   (events/save-personal attribute new-value))}]))

(defn password-view
  []
  [:div
   [:div
    {:class "flex flex-col justify-start items-start gap-4"}
    [:div
     {:class "font-bold text-[18px]"}
     (loc/tr :password)]
    [button/sm-primary-solid
     {:on-click (fn [_]
                  (modals/open-modal! change-password/modal-key {}))
      :dom-id "profile-change-password"}
     (loc/tr :change-password)]]])

(defn sandbox
  []
  (let [account-preferences (:account/preferences @session/account)
        sandbox-disabled? (boolean (:disabled-sandbox account-preferences))]
    [:div

     [:div
      [:input
       (merge {:type      :checkbox
               :value     sandbox-disabled?
               :read-only true
               :on-click  (fn [e]
                            (account-actions/update-preferences {:disabled-sandbox
                                                                 (not sandbox-disabled?)})
                            (dg/prevent-and-stop e)
                            false)
               :checked   sandbox-disabled?})]
      (loc/tr :personal-profile/disable-sandbox?)]]))

(defn insider-group-no-feature
  []
  [:div
   [:div
    {:class "flex flex-col gap-2"}
    [:div
     {:class "font-bold text-[18px]"}
     (loc/tr :insider-groups-title)]
    [:ul
     {:class "pt-3 flex flex-col gap-2 list-disc list-inside"}
     [:li
      (loc/tr :insider-group-bullet-1)]
     [:li
      (loc/tr :insider-group-bullet-2)]
     [:li
      (loc/tr :insider-group-bullet-3)]]
    [:div
     {:class "mt-4"}
     [button/sm-primary-solid
      {:disabled? true
       :tooltip   [:span
                   (loc/tr :personal-feature-not-available-1)
                   [:a {:href (str "mailto:" (loc/tr :support-email-address))}
                    (loc/tr :support-email-address)]
                   (loc/tr :personal-feature-not-available-2)]}
      (loc/tr :unlock-insider-groups)]]]])

(defn insider-group-memberships
  [insider-groups state*]
  [:div
   [:div
    {:class "flex flex-col gap-2"}
    [:div
     {:class "font-bold text-[18px]"}
     (loc/tr :insider-groups-member-title)]
    [:ul
     {:class "pt-3 flex flex-wrap gap-2"}
     (->> insider-groups
          (map (fn [{:keys [db/id] :as ig}]
                 ^{:key (str "insider-group-" id)}
                 [:li
                  [popover/view
                   {:open?          (get-in @state* [:popover-open? id] false)
                    :on-open-change (fn [open?]
                                      (swap! state* assoc-in [:popover-open? id] open?))}
                   [:span
                    {:class    "inline-block py-2 px-3 rounded-full
                                 bg-base-regular hover:bg-base-gloomy
                                 cursor-pointer "
                     :on-click (fn [e]
                                 (swap! state* assoc-in [:popover-open? id] (not (get-in @state* [:popover-open? id])))
                                 (fu/stop-n-prevent e))}
                    (:insider-group/name ig)]
                   [insider-activity/popup-view id]]]))
          (doall))]]])

(defn insider-group-no-memberships
  []
  [:div
   [:div
    {:class "flex flex-col gap-2"}
    [:div
     {:class "font-bold text-[18px]"}
     (loc/tr :insider-groups-not-member-title)]]])

(defn insider-groups
  []
  (let [insider-groups @session/all-insider-groups
        state* (r/atom {:open? false})
        feature-active? (plan/available :feature/insider-groups @session/tenant @session/role)]
    (fn []
      (cond (not feature-active?)
            [insider-group-no-feature]
            (empty? insider-groups)
            [insider-group-no-memberships]
            :else
            [insider-group-memberships insider-groups state*]))))

(defn edit-row
  [{:keys [_on-save attribute dom-id value]}]
  (let [edited-value-state (r/atom value)]
    (fn [{:keys [on-save value editing-state]}]
      [:div
       {:class "flex justify-between items-center gap-4"
        :id    dom-id}
       [simple-text-input/view
        {:class       "w-full"
         :dom-id      (str dom-id "-input")
         :auto-focus? true
         :on-change   (fn [v]
                        (reset! edited-value-state v))
         :value       @edited-value-state}]

       [:div
        {:class ""}
        [:div
         {:class "flex gap-2"}
         [button/sm-secondary-hollow
          {:id       (str dom-id "-cancel-btn")
           :on-click (fn []
                       (reset! editing-state nil))}
          (loc/tr :cancel)]
         [button/sm-primary-solid
          {:id        (str dom-id "-save-btn")
           :on-click  (fn []
                        (on-save attribute @edited-value-state)
                        (reset! editing-state nil))
           :disabled? (= value @edited-value-state)}
          (loc/tr :save)]]]])))

(defn read-row
  [{:keys [field-title attribute dom-id editing-state value]}]
  [:div
   {:class "flex justify-between items-center
            text-base-light-text"}
   [:div
    {:class "flex flex-col gap-2"}
    [:div
     {:class "font-semibold"}
     field-title]
    [:div
     {:class "text-base-light-text-paler"
      :id    (str dom-id "-value")}
     value]]
   (when-not (= attribute :account/email)
     [:div
      [button/sm-secondary-solid
       {:disabled? (boolean @editing-state)
        :id        (str dom-id "-edit-btn")
        :on-click  (fn []
                     (when-not @editing-state
                       (reset! editing-state attribute)))}
       (loc/tr :edit)]])])


(defn editable-row
  [{:keys [editing-state attribute] :as args}]
  (if (= attribute @editing-state)
    [edit-row args]
    [read-row args]))

(defn photo-row
  []
  (let [refs (atom {})
        local-state (r/atom {})]
    (fn []
      (let [{:keys [account/picture-url account/first-name account/last-name]} @session/account]
        [:div
         {:class "mb-4
                  flex flex-col md:flex-row justify-between md:items-center gap-8
                  text-base-light-text font-md"}
         [:div
          {:class "flex gap-4 items-center"}
          [:div
           {:class    "flex items-center justify-center
                       text-2xl text-secondary-regular-text uppercase font-semibold
                       rounded-full
                       relative
                       overflow-hidden
                       group"
            :on-click (fn [e]
                        (dg/stop-propagation e)
                        (swap! local-state update :show-tools? not))}

           (when picture-url
             [:div
              {:class    "hidden group-hover:block
                                p-2 absolute left-2/4 top-2/4 -translate-y-1/2 -translate-x-1/2
                                text-3xl font-semibold text-secondary-regular-text-paler hover:text-secondary-light-text-accent disabled:text-disabled-regular-text
                                cursor-pointer
                                rounded"
               :on-click (fn [_]
                           (cropper/open-cropper-modal!
                            {:entity        @session/account
                             :attribute     :account/picture-url
                             :target-width  110
                             :target-height 110
                             :round?        true
                             :on-done!      (fn [_]
                                              (swap! local-state dissoc :show-tools?))}))}
              [:i
               {:class "fas fa-camera"}]])

           [:div
            {:class "hidden"}
            [:input
             {:class     "absolute w-[100px] h-[100px] z-0"
              :type      "file"
              :accept    ".jpg, .jpeg, .png"
              :ref       (fn [component]
                           (when-not (= component (:file-input @refs))
                             (swap! refs assoc :file-input component)))
              :on-click  (fn [e]
                           (dg/stop-propagation e))
              :on-change (fn [e]
                           (events/modify-file! e)
                           (events/save!)
                           (oops/oset! (:file-input @refs) "value" "")
                           (swap! local-state dissoc :show-tools?))}]]
           [avatar/view
            {:image-url picture-url
             :fallback  (str " " (first first-name) (first last-name))
             :size      :large
             :font-size :large}]]
          [:div
           {:class "mb-0
                    flex flex-col justify-center items-start gap-2"}
           [:div
            {:class "font-bold"}
            (loc/tr :upload-profile-photo)]
           [:div
            {:class "mb-0
                     text-base-light-text-paler text-sm"}
            (loc/tr :helps-teammates-recognize)]]]
         [:div
          {:class "flex gap-4"}
          [:div
           {:id       "profile-upload-image"
            :class    "px-4 py-2 mb-0
                    text-sm font-semibold text-md rounded text-secondary-light-text hover:text-secondary-light-text-accent disabled:text-disabled-regular-text
                    border border-secondary-light-border disabled:border-disabled-regular-border
                    bg-secondary-light disabled:bg-regular
                    rounded
                    focus:scale-[0.97]
                    cursor-pointer"
            :on-click (fn [e]
                        (dg/stop-propagation e)
                        (let [file-input (:file-input @refs)]
                          (when file-input
                            (oops/ocall file-input "click"))))}
           (loc/tr :click-to-upload)]

          (when picture-url
            [:div
             {:class    "px-4 py-2 mb-0
                      text-sm font-semibold text-md rounded text-danger-light-text hover:text-danger-light-text-accent disabled:text-disabled-regular-text
                      border border-danger-light-border-paler disabled:border-disabled-regular-border
                      bg-danger-light disabled:bg-regular
                      rounded
                      focus:scale-[0.97]
                      cursor-pointer"
              :on-click (fn [_]
                          (account-actions/*->update-personal
                           {:db/id               (:db/id @session/account)
                            :account/picture-url nil}
                           (fn []
                             (oops/oset! (:file-input @refs) "value" "")
                             (swap! local-state dissoc :show-tools?))
                           (fn [err]
                             (logging/error "failed to clear avatar" err))))}
             (loc/tr :remove-image)])]]))))

(defn application-analytics
  []
  (let [account-preferences (:account/preferences @session/account)
        application-analytics? (:application-analytics? account-preferences)]
    [:div
     {:class "p-3 flex items-center justify-between border border-solid border-base-light-border-paler rounded"}
     [:div
      {:class "text-base-light-text font-bold"}
      (loc/tr :feature/application-analytics)]
     [:div
      [toggle/toggle
       {:checked?  application-analytics?
        :dom-id "profile-preference-analytics"
        :on-change (fn [application-analytics?]
                     (account-actions/update-preferences {:application-analytics?
                                                          application-analytics?}))}]]]))

(defn debug-options
  [view-state]
  (let [debug? (:print-debug? @view-state)]
    [:div
     {:class "p-3 flex items-center justify-between border border-solid border-base-light-border-paler rounded"}
     [:div
      {:class "text-base-light-text font-bold"}
      (loc/tr :personal-profile/technical-logs)]
     [:div
      [:div
       [toggle/toggle
        {:checked?  debug?
         :dom-id "profile-preference-print-debug"
         :on-change (fn [_]
                      (app-db/toggle-debug!)
                      (swap! view-state assoc :print-debug? (logging/print-debug?))
                      (deref view-state))}]]]]))

(defn recalculate-permissions
  []
  (let [recalculating? (r/atom false)]
    (fn []
      [:div
       {:class "p-3 flex items-center justify-between border border-solid border-base-light-border-paler rounded"}
       [:div
        {:class "flex flex-col gap-3"}
        [:div
         {:class "text-base-light-text font-bold"}
         (loc/tr :personal-profile/recalculate-permissions-title)]
        [:span
         {:class "text-base-light-text-paler"}
         (loc/tr :personal-profile/recalculate-permissions-info)]]
       [button/sm-secondary-hollow
        {:disabled? @recalculating?
         :on-click (fn [_]
                     (reset! recalculating? true)
                     (account-actions/*->recalculate-permissions
                      {}
                      (fn [_]
                        (reset! recalculating? false)
                        (rf/dispatch [::toast/success (loc/tr :personal-profile/recalculate-permissions-success)]))
                      (fn [e]
                        (reset! recalculating? false)
                        (logging/error (str "error! recalculating permissions " e))
                        (rf/dispatch [::toast/error {:message (loc/tr :personal-profile/recalculate-permissions-error)}]))))}
        (if @recalculating?
          (loc/tr :loading)
          (loc/tr :personal-profile/recalculate-permissions-action))]])))

(defn show-entity-esign-version-setting
  []
  (let [attribute :show-esign-process-version?
        account-preferences (:account/preferences @session/account)
        checked? (get account-preferences attribute)]
    [:div
     {:class "p-3 flex items-center justify-between border border-solid border-base-light-border-paler rounded"}
     [:div
      {:class "text-base-light-text font-bold"}
      "Show entity e-sign version"]
     [:div
      [:div
       [toggle/toggle
        {:checked?  checked?
         :dom-id "profile-preference-entity-e-sign"
         :on-change (fn [checked?]
                      (account-actions/update-preferences {attribute checked?}))}]]]]))

(defn details
  []
  (let [editing-state (r/atom nil)]
    (fn []
      [:div
       {:class "flex flex-col"}
       [photo-row]
       [:div
        {:class "*:py-4 grid grid-cols-1
                 divide-y divide-solid divide-base-light-border-paler"}
        [editable-row
         {:on-save       events/save-personal
          :field-title   (loc/tr :user-name)
          :editing-state editing-state
          :attribute     :account/email
          :value         (:account/email @db/account)}]

        (doall
         (for [attribute [:account/first-name :account/last-name :account/phone-number
                          :account.linkedin/profile-url]]
           (let [dom-id (str "personal-profile-field-" (name attribute))
                 account (merge @db/account (:edit-account @db/state))]
             ^{:key dom-id}
             [editable-row
              {:on-save       events/save-personal
               :dom-id        dom-id
               :editing-state editing-state
               :attribute     attribute
               :field-title   (loc/tr attribute)
               :value         (get account attribute)}])))]])))

(defn- user-session-timeout-options
  "Build dropdown options filtered by tenant max, with localized labels"
  [max-tenant-timeout]
  (->> (session-timeout/filter-options-by-max max-tenant-timeout)
       (map (fn [{:keys [value key]}]
              {:value value
               :label (loc/tr key)}))))

(defn- user-session-timeout-dropdown
  "Dropdown for user session timeout preference"
  [{:keys [attribute label tooltip-text max-tenant-timeout]}]
  (let [account @session/account
        current-value (get account attribute)
        options (user-session-timeout-options max-tenant-timeout)
        current-option (->> options
                            (filter #(= (:value %) current-value))
                            first)]
    [:div {:class "flex flex-col gap-px"}
     [:div {:class "flex items-center gap-1"}
      [:label {:class "font-semibold text-sm leading-none m-0"} label]
      [tooltip/view
       {}
       [:i.fal.fa-info-circle.text-base-light-text-paler.cursor-help.text-sm.m-0]
       [:div {:class "max-w-sm text-sm whitespace-pre-line"} tooltip-text]]]
     [labelled-input/view
      {:data-type :data.type/keyword
       :value     current-option
       :options   options
       :on-change (fn [new-option]
                    (let [new-value (if (map? new-option)
                                      (:value new-option)
                                      new-option)]
                      (when-not (= new-value current-value)
                        (account-actions/*->update-personal
                         {:db/id    (:db/id account)
                          attribute new-value}
                         (fn [_] (logging/info "Session timeout preference updated"))
                         (fn [_] (logging/error "Failed to update session timeout preference"))))))}]]))

(defn- has-mcp-connector-module-access?
  []
  (and (session/has-feature-access? :feature/mcp-connector)
       (or (session/has-module-access? @session/account :module/mcp-connector-read)
           (session/has-module-access? @session/account :module/mcp-connector-write))))

(defn- user-session-timeout-settings
  "User-level session timeout settings section.
   Only shown when tenant has been migrated to keyword-based timeouts."
  []
  (let [tenant @session/tenant
        has-addin-access? (or (and (session/has-feature-access? :feature/outlook-addin)
                                   (session/has-module-access? @session/account :module/outlook-addin))
                              (has-mcp-connector-module-access?))]
    [:div {:class "flex flex-col gap-6"}
     ;; Section header
     [:div {:class "flex flex-col gap-2"}
      [:div {:class "font-bold text-lg"} (loc/tr :security/session-timeout-title)]
      [:p {:class "text-base-light-text-paler text-sm whitespace-pre-line m-0"}
       (loc/tr :security/session-timeout-description)]]

     ;; Web application timeout
     [user-session-timeout-dropdown
      {:attribute          :account/web-session-timeout-kw
       :label              (loc/tr :profile/web-session-timeout-label)
       :tooltip-text       (loc/tr :profile/web-session-timeout-tooltip)
       :max-tenant-timeout (:tenant/web-session-timeout-kw tenant)}]

     ;; Add-in timeout (behind feature flag)
     (when has-addin-access?
       [user-session-timeout-dropdown
        {:attribute          :account/addin-session-timeout-kw
         :label              (loc/tr :profile/addin-session-timeout-label)
         :tooltip-text       (loc/tr :profile/addin-session-timeout-tooltip)
         :max-tenant-timeout (:tenant/addin-session-timeout-kw tenant)}])]))

(defn security
  []
  (let [has-new-timeout? (some? (:tenant/web-session-timeout-kw @session/tenant))]
    [:div
     {:class "flex flex-col gap-6 pb-3"}
     (when-not (:tenant/block-password-login @session/tenant)
       [password-view])
     ;; Only show user session timeout settings if tenant has been migrated
     (when has-new-timeout?
       [user-session-timeout-settings])]))

(defn nylas-privacy
  [_state]
  (let [local-state (r/atom {})
        toggle-past! (fn [_]
                       (swap! local-state update :update-past? not))]
    (fn []
      (let [account @session/account
            default-nylas-privacy (->> account
                                       :account/default-nylas-privacy)
            {:keys [update-past?]} @local-state
            private? (= default-nylas-privacy :privacy-status/private)
            [current-privacy-text new-privacy-text]
            (if private?
              [(loc/tr :nylas-privacy/private) (loc/tr :nylas-privacy/public)]
              [(loc/tr :nylas-privacy/public) (loc/tr :nylas-privacy/private)])
            options (->> [:nylas-privacy/apply-change-to-future :nylas-privacy/apply-change-to-all-1]
                         (map (fn [p]
                                {:value   p
                                 :content (loc/tr p)})))]
        [:div
         [:div
          {:class "pt-4 flex flex-col gap-4"}
          [:div
           (loc/tr :nylas-privacy/info-1)
           [:span
            current-privacy-text]
           (loc/tr :nylas-privacy/info-2)
           [:span
            new-privacy-text]
           (loc/tr :nylas-privacy/info-3)]

          [radio-group/radio-group-v2
           {:checked     update-past?
            :orientation :vertical
            :on-change   toggle-past!}
           options]

          ;; [:div
          ;;  [:label
          ;;   {:class-name "zap-inlined-radiobox-label mb-3"}
          ;;   [:input.delete-confirmation-modal__radiobox.zap-inlined-radiobox__green
          ;;    {:name      "zap-choice-confirmation-radio"
          ;;     :type      "radio"
          ;;     :checked   (not update-past?)
          ;;     :value     (not update-past?)
          ;;     :on-change toggle-past!}]
          ;;   (loc/tr :nylas-privacy/apply-change-to-future)
          ;;   [:span
          ;;    {:class-name "radiomark"}]]
          ;;  [:label
          ;;   {:class-name "zap-inlined-radiobox-label mb-4"}
          ;;   [:input.delete-confirmation-modal__radiobox.zap-inlined-radiobox__green
          ;;    {:name      "zap-choice-confirmation-radio"
          ;;     :type      "radio"
          ;;     :checked   (boolean update-past?)
          ;;     :value     (boolean update-past?)
          ;;     :on-change toggle-past!}]
          ;;   (loc/tr :nylas-privacy/apply-change-to-all-1)
          ;;   [:span.nylas-privacy-highlight
          ;;    new-privacy-text]
          ;;   (loc/tr :nylas-privacy/apply-change-to-all-2)
          ;;   [:span
          ;;    {:class-name "radiomark"}]]]

          (if (:processing? @local-state)
            (loc/tr :processing)

            [:div
             [button/sm-secondary-hollow
              {:on-click (fn [_]
                           (swap! local-state assoc :processing? true)
                           (events/update-nylas-privacy!
                            account
                            (if private? :privacy-status/public :privacy-status/private)
                            update-past?
                            (fn [_resp]
                              (fu/timeout 500
                                          (fn []
                                            (swap! local-state dissoc :processing?)
                                            (deref local-state))))
                            (fn [err]
                              (fu/timeout 500
                                          (fn []
                                            (swap! local-state dissoc :processing?)
                                            (deref local-state)))
                              (logging/error (str "error! updating default email privacy " err)))))}
              (loc/tr :nylas-privacy/change-privacy-button [new-privacy-text])]])]]))))

(defn nylas-sync-contacts
  []
  (let [local-state (r/atom {})]
    (fn []
      (let [account @session/account
            processing? (:processing? @local-state)
            sync-enabled? (or (:temp-sync? @local-state)
                              (->> account
                                   :account/nylas-sync-contacts?))
            on-ok! (fn [_]
                     (js/setTimeout #(swap! local-state dissoc :temp-sync? :processing?)
                                    1000))
            on-error! (fn [_]
                        (js/setTimeout #(swap! local-state dissoc :temp-sync? :processing?)
                                       1000))]
        (cond
          processing?
          [:div
           {:class "mt-4 mb-3"}
           [:p (loc/tr :processing)]
           [:i.fal.fa-spinner.fa-pulse.fa-3x]]
          sync-enabled?
          [:div
           [:div
            {:class "my-3 inline-flex flex-col gap-3"}
            [:p (loc/tr :nylas-sync-contacts/info-enabled)]
            [:div
             [button/sm-primary-solid
              {:class    "w-auto"
               :on-click (fn [_]
                           (swap! local-state assoc
                                  :temp-sync? false
                                  :processing? true)
                           (events/update-nylas-sync-contacts false on-ok! on-error!))}
              (loc/tr :nylas-settings/disable-contacts-sync)]]]]
          :else
          [:div
           [:div
            {:class "my-3 inline-flex flex-col gap-3"}
            [:p
             (loc/tr :nylas-sync-contacts/info-disabled)]
            [:div
             {:class "inline-flex"}
             [button/sm-primary-solid
              {:on-click (fn [_]
                           (swap! local-state assoc
                                  :temp-sync? true
                                  :processing? true)
                           (events/update-nylas-sync-contacts true on-ok! on-error!))}
              (loc/tr :nylas-settings/enable-contacts-sync)]]]])))))

(defn email-preferences
  []
  [:div
   {:class "pb-4"}
   [:div
    {:class "flex flex-col gap-4"}
    [:div
     {:class "flex flex-col gap-4"}
     [:div
      {:class "font-bold text-[18px]"}
      (loc/tr :personal-profile/alternative-addresses-title)]
     [:span
      {:class "text-base-light-text-paler"}
      (loc/tr :personal-profile/alternative-addresses-info)]]
    (doall
     (for [attribute [:account/alt-email :account/tertiary-email]]
       ^{:key (str "personal-profile-field-" attribute)}
       [attribute-form {:attribute attribute}]))]])

(defn nylas-preferences
  []
  (let [{:keys [account/nylas-account-id account/nylas-email]} @session/account
        disabled? (not (plan/available :feature/nylas-integration @session/tenant @session/role))]
    [:div
     {:class "pb-4"}
     [:div
      {:class "py-4 flex flex-col gap-4"}
      [:div
       {:class "font-bold text-[18px]"}
       (loc/tr :personal-profile/email-connection-title)]
      [:span
       {:class "text-base-light-text-paler"}
       (loc/tr (if nylas-account-id
                 :personal-profile/email-sync-info-connected
                 :personal-profile/email-sync-info))]]
     (if nylas-account-id
       [:div
        {:class "flex flex-col gap-3"}
        [:span
         {:class "font-bold"}
         nylas-email]
        [:div
         {:class "inline-flex"}
         [button/sm-secondary-hollow
          {:on-click events/disconnect-nylas
           :dom-id   "email-sync-disconnect-account-button"}
          (loc/tr :personal-profile/email-sync-disconnect-account-button)]]
        [nylas-privacy]
        [nylas-sync-contacts]]
       [button/sm-primary-solid
        {:on-click  events/connect-nylas
         :dom-id    "profile-connect-nylas-button"
         :tooltip   (when disabled?
                      [:span
                       (loc/tr :personal-feature-not-available-1)
                       [:a {:href (str "mailto:" (loc/tr :support-email-address))}
                        (loc/tr :support-email-address)]
                       (loc/tr :personal-feature-not-available-2)])
         :disabled? disabled?}
        (loc/tr :personal-profile/email-sync-connect-account-button)])]))

(defn web-form-link
  [url]
  [:div
   (if-not (session/is-mobile?)
     [:div
      [:div
       {:class "pt-1 pb-1 ps-4 pe-1 flex items-center justify-between bg-base-light rounded rounded-r-full rounded-l"}
       [:span {:class "w-full flex items-center justify-between gap-3
                             font-semibold text-complement-regular-text"}
        [:span
         {:class "text-complement-regular-text truncate "} url]
        [:span
         {:class "flex gap-3"}
         [button/sm-primary-solid
          {:class    "rounded-full px-6"
           :on-click (fn [e]
                       (clipboard/copy-to url)
                       (dg/prevent-and-stop e))
           :tooltip  (loc/tr :copy-to-clipboard)}
          "Copy"]
         ;; TODO Click on link itself will open new tab
         ;; [:i.fal.fa-external-link
         ;;  {:on-click (fn [e]
         ;;               (.open js/window url "_blank")
         ;;               (.dg/prevent-and-stop e))}]
         ;;TODO when clicking from my-profile,
         ;;     forward to [:new-settings-web-form] [{:webentry-id webentry-id}]
         ;;     (hide when in new-settings-web-form):
         #_(when (session/is-admin?)
             [:i.fal.fa-cog
              {:on-click (fn [e]
                           (dg/prevent-and-stop e)
                           (routing/set-view! [:webentry :token apikey]
                                              {:open-settings? true}))}])]]



       " "]
      [:a
       {:href   url
        :target "_blank"
        :class  "py-2 flex items-center gap-3 text-base-regular-text hover:text-base-regular-text-contrast font-bold"}
       [:i.fas.fa-external-link-alt] "View webform"]]
     [:div
      [button/primary-solid
       {:on-click (partial clipboard/copy-to url)}
       (loc/tr :copy-to-clipboard)]])])

(defn web-form-address
  [css-class webentry hide-label?]
  (let [apikey (:webentry/apikey webentry)
        origin (str (oops/oget (dg/location) "origin") "/")
        webentry-address-start (str origin "webentry/")
        url (str webentry-address-start apikey)]
    [:div
     {:class-name (fu/cs css-class)}
     (when-not hide-label?
       [:h3
        (if-let [webentry-name (:webentry/display-name webentry)]
          webentry-name
          (loc/tr :unnamed-webform))])
     [web-form-link url]]))

(defn reminder-emails
  []
  [:<>
   [:div
    {:class "p-3 flex items-baseline justify-between border border-solid border-base-light-border-paler rounded"}
    [:div
     {:class "flex flex-col gap-3"}
     [:div
      {:class "text-base-light-text font-bold"}
      (loc/tr :reminder-emails)]
     [:span
      {:class "text-base-light-text-paler"}
      (loc/tr :reminder-emails-info)]]
    [:div
     {:class "flex justify-end"}
     [toggle/toggle
      {:checked?  (:account/reminder-emails? @session/account)
       :dom-id "profile-preference-reminder-emails"
       :on-change (fn [enable-reminder-emails?]
                    (account-actions/*->update-personal
                     {:db/id                    (:db/id @session/account)
                      :account/reminder-emails? enable-reminder-emails?}
                     (fn [_])
                     (fn [e]
                       (logging/error (str "error! updating account reminder emails " e)))))}]]]])

(defn task-notifications?
  []
  [:<>
   [:div
    {:class "p-3 flex items-baseline justify-between border border-solid border-base-light-border-paler rounded"}
    [:div
     {:class "flex flex-col gap-3"}
     [:div
      {:class "text-base-light-text font-bold"}
      (loc/tr :send-task-notifications)]
     [:span
      {:class "text-base-light-text-paler"}
      (loc/tr :task-notification-emails-info)]]
    [:div
     {:class "flex justify-end"}
     [toggle/toggle
      {:checked?  (not= :no
                        (get-in @session/account
                                [:account/preferences :send-task-notifications?]))
       :dom-id "profile-preference-send-task-notifications"
       :on-change (fn [send?]
                    (js/console.log "send" send?)
                    (account-actions/*->update-preferences
                     {:account/preference {:send-task-notifications? (if send?
                                                                       :yes
                                                                       :no)}}
                     (fn [_])
                     (fn [e]
                       (logging/error (str "error! updating account task notification emails "
                                           e)))))}]]]])

(defn editable-grid
  []
  (let [account-preferences (:account/preferences @session/account)
        editable-grid? (:editable-grid? account-preferences)]
    [:div
     {:class "p-3 flex items-baseline justify-between border border-solid border-base-light-border-paler rounded"}
     [:div
      {:class "flex flex-col gap-3"}
      [:div
       {:class "text-base-light-text font-bold"}
       (loc/tr :feature/editable-grid)]
      [:span
       {:class "text-base-light-text-paler"}
       (loc/tr :editable-grid-info)]]
     [:div
      {:class "flex justify-end"}
      [toggle/toggle
       {:checked?  editable-grid?
        :dom-id "profile-preference-editable-grid"
        :on-change (fn [editable-grid?]
                     (account-actions/update-preferences {:editable-grid? editable-grid?}))}]]]))

(defn customizable-nev
  []
  (let [account-preferences (:account/preferences @session/account)
        customizable-nev? (:customizable-nev? account-preferences)]
    [:div
     {:class "p-3 flex items-baseline justify-between border border-solid border-base-light-border-paler rounded"}
     [:div
      {:class "flex flex-col gap-3"}
      [:div
       {:class "text-base-light-text font-bold"}
       (loc/tr :feature/customizable-nev)]
      [:span
       {:class "text-base-light-text-paler"}
       (loc/tr :customizable-nev?-info)]]
     [toggle/toggle
      {:checked?  customizable-nev?
       :dom-id "profile-preference-customizable-nev"
       :on-change (fn [customizable-nev?]
                    (account-actions/update-preferences {:customizable-nev?
                                                         customizable-nev?}))}]]))

(defn contact-emails-in-activity-log?
  []
  (let [account-preferences (:account/preferences @session/account)
        show-in-activity-log? (not (:hide-contacts-emails-from-timeline? account-preferences))]
    [:div
     {:class "p-3 flex items-baseline justify-between border border-solid border-base-light-border-paler rounded"}
     [:div
      {:class "flex flex-col gap-3"}
      [:div
       {:class "text-base-light-text font-bold"}
       (loc/tr :contact-emails-in-activity-log?)]
      [:span
       {:class "text-base-light-text-paler"}
       (loc/tr :contact-emails-in-activity-log?-info)]]

     [:div
      {:class "flex justify-end"}
      [toggle/toggle
       {:checked?  show-in-activity-log?
        :dom-id "profile-preference-hide-contact-emails"
        :on-change (fn [new-checked-value]
                     (account-actions/update-preferences {:hide-contacts-emails-from-timeline?
                                                          (not new-checked-value)}))}]]]))

(defn new-grid-2023
  []
  (let [account-preferences (:account/preferences @session/account)
        new-grid-2023? (:new-grid-2023? account-preferences)]
    [:<>
     [:div
      {:class "p-3 flex items-center justify-between border border-solid border-base-light-border-paler rounded"}
      [:div
       {:class "flex flex-col gap-3"}
       [:div
        {:class "text-base-light-text font-bold"}
        (loc/tr :feature/new-grid-2023)]]
      [:div
       {:class "flex justify-end"}
       [toggle/toggle
        {:checked?  new-grid-2023?
         :dom-id "profile-preference-new-grid-2023"
         :on-change (fn [new-grid-2023?]
                      (account-actions/update-preferences {:new-grid-2023? new-grid-2023?}))}]]]]))

(defn performance
  []
  (let [account-preferences (:account/preferences @session/account)
        skip-indexeddb? (:skip-indexeddb? account-preferences)]
    [:<>
     [:div
      {:class "p-3 flex items-center justify-between border border-solid border-base-light-border-paler rounded"}
      [:div
       {:class "flex flex-col gap-3"}
       [:div
        {:class "text-base-light-text font-bold"}
        (loc/tr :preference/skip-indexeddb?)]]
      [:div
       [toggle/toggle
        {:checked?  skip-indexeddb?
         :dom-id "profile-preference-skip-indexeddb"
         :on-change (fn [new-value]
                      (account-actions/update-preferences {:skip-indexeddb? new-value}))}]]]]))

(defn limited-data-sync
  []
  (let [account-preferences (:account/preferences @session/account)
        limited-data-sync? (:limited-data-sync? account-preferences)]
    [:<>
     [:div
      {:class "p-3 flex items-center justify-between border border-solid border-base-light-border-paler rounded"}
      [:div
       {:class "text-base-light-text font-bold"}
       (loc/tr :feature/limited-data-sync)]
      [:div
       [toggle/toggle
        {:checked?  limited-data-sync?
         :dom-id "profile-preference-limited-data-sync"
         :on-change (fn [limited-data-sync?]
                      (account-actions/update-preferences {:limited-data-sync? limited-data-sync?}))}]]]]))

(defn company-nev-toggle-2024
  []
  (let [account-preferences (:account/preferences @session/account)
        ;; FIXME: This is both company and person so name is misleading
        new-company-viewer-preview-2024? (:new-company-viewer-preview-2024? account-preferences)]
    [:<>
     [:div
      {:class "p-3 flex items-baseline justify-between border border-solid border-base-light-border-paler rounded"}
      [:div
       {:class "flex flex-col gap-3"}
       [:div
        {:class "flex items-center gap-4"}
        [:div
         {:class "text-base-light-text font-bold"}
         (loc/tr :feature/new-company-viewer-preview-2024)]
        [:div
         {:class "rounded-full px-4 border bg-warning-regular"}
         [:span
          {:class "text-sm m-0 text-warning-regular-text font-semibold"}
          (loc/tr :beta)]]]
       [:span
        {:class "text-base-light-text-paler"}
        (loc/tr :new-entity-viewer-preview-2024-info)]]

      [:div

       [toggle/toggle
        {:checked?  new-company-viewer-preview-2024?
         :dom-id "profile-preference-company-preview-2024"
         :on-change (fn [new-company-viewer-preview-2024?]
                      (account-actions/update-preferences
                       {:new-company-viewer-preview-2024? new-company-viewer-preview-2024?
                        :classic-entity-viewer-enabled?   (not new-company-viewer-preview-2024?)}))}]]]]))

(defn options
  []
  [:div
   [:div
    [:div
     {:class "flex flex-col gap-3"}                                                                 ;;Hardcoded to alphabetical order
     (when (session/has-feature-access? :feature/customizable-nev)
       [customizable-nev])
     (when (session/has-feature-access? :feature/performance-testing)
       [performance])
     (when (and (session/has-feature-access? :feature/editable-grid)
                (not (session/is-read-only-user?)))
       [editable-grid])
     (when (session/has-feature-access? :feature/limited-data-sync)
       [limited-data-sync])
     (when (session/has-feature-access? :feature/new-grid-2023)
       [new-grid-2023])
     (when-not (session/is-read-only-user?)
       [reminder-emails])
     (when-not (tenant-customizations/capman?)
       [contact-emails-in-activity-log?])
     (when-not (session/is-read-only-user?)
       [task-notifications?])
     (when (session/nev-v3?)
       [company-nev-toggle-2024])
     (when (and (session/has-feature-access? :feature/application-analytics)
                (not (session/is-read-only-user?)))
       [application-analytics])
     [recalculate-permissions]
     [debug-options db/state]

     (when (and (session/has-feature-access? :feature/entity-esign)
                (not (session/is-read-only-user?)))
       [show-entity-esign-version-setting])]]])

(defn contents
  [_tab-key]
  ;; initial load
  (let [load-tokens (fn [max-count]
                      (account-actions/*->tokens {:max-count max-count}
                                                 (fn [resp]
                                                   (swap! db/state assoc :tokens (:body resp)))
                                                 (fn [err]
                                                   (logging/error "Failed to load tokens " err))))]
    (load-tokens 25)
    (r/create-class
     {:component-did-mount
      (fn [_]
        (swap! db/state assoc
               :print-debug?
               (logging/print-debug?)))
      :component-will-unmount
      (fn [_]
        (reset! db/mfa {}))
      :reagent-render
      (fn [tab-key]
        (let [has-addin-access? (or (and (session/has-feature-access? :feature/outlook-addin)
                                         (session/has-module-access? @session/account :module/outlook-addin))
                                    (has-mcp-connector-module-access?))
              ;; Guard: if user navigates directly to connected-addins without access,
              ;; redirect to my-details and update the URL to match
              needs-redirect? (and (= tab-key :connected-addins) (not has-addin-access?))
              tab-key (if needs-redirect? :my-details tab-key)]
          (when needs-redirect?
            (r/after-render
             #(rf/dispatch [::events/side-nav-clicked [:personal-profile :tab-key :my-details]])))
        [:div
         {:class "pb-8
                  text-base-light-text
                  bg-base-light"}
         [:div
          {:class "max-w-5xl m-auto px-4 py-6"}
          [:h4
           {:class "pb-6 text-xl font-bold"
            :id    "profile-preferences-info"}
           (loc/tr :my-info-and-personal-preferences)]
          [:div
           {:class "md:grid md:grid-cols-10 gap-4"}
           [:div
            {:class "md:col-span-2 relative"}
            [:div
             {:class "sticky mb-6 top-0 flex md:flex-col gap-1 md:gap-4
                      text-base-light-text"}
             [link/side-nav-link {:active?  (= tab-key :my-details)
                                  :href     "#"
                                  :dom-id   (loc/tr :my-details)
                                  :on-click (fn [e]
                                              (dg/prevent-default e)
                                              (when-not (= tab-key :my-details)
                                                (rf/dispatch [::events/side-nav-clicked [:personal-profile :tab-key :my-details]])))}
              (loc/tr :my-details)]
             [link/side-nav-link {:active?  (= tab-key :email-preferences)
                                  :href     "#"
                                  :dom-id   (loc/tr :email-preferences)
                                  :on-click (fn [e]
                                              (dg/prevent-default e)
                                              (when-not (= tab-key :email-preferences)
                                                (rf/dispatch [::events/side-nav-clicked [:personal-profile :tab-key :email-preferences]])))}
              (loc/tr :email-preferences)]
             [link/side-nav-link {:active?  (= tab-key :my-preferences)
                                  :href     "#"
                                  :dom-id   (loc/tr :my-preferences)
                                  :on-click (fn [e]
                                              (dg/prevent-default e)
                                              (when-not (= tab-key :my-preferences)
                                                (rf/dispatch [::events/side-nav-clicked [:personal-profile :tab-key :my-preferences]])))}
              (loc/tr :my-preferences)]
             [link/side-nav-link {:active?  (= tab-key :security)
                                  :href     "#"
                                  :dom-id   (loc/tr :security)
                                  :on-click (fn [e]
                                              (dg/prevent-default e)
                                              (when-not (= tab-key :security)
                                                (rf/dispatch [::events/side-nav-clicked [:personal-profile :tab-key :security]])))}
              (loc/tr :security)]
             [link/side-nav-link {:active?  (= tab-key :insider-groups)
                                  :href     "#"
                                  :dom-id   (loc/tr :insider-groups)
                                  :on-click (fn [e]
                                              (dg/prevent-default e)
                                              (when-not (= tab-key :insider-groups)
                                                (rf/dispatch [::events/side-nav-clicked [:personal-profile :tab-key :insider-groups]])))}
              (loc/tr :insider-groups)]
             (when has-addin-access?
               [link/side-nav-link {:active?  (= tab-key :connected-addins)
                                    :href     "#"
                                    :dom-id   "connected-addins"
                                    :on-click (fn [e]
                                                (dg/prevent-default e)
                                                (when-not (= tab-key :connected-addins)
                                                  (rf/dispatch [::events/side-nav-clicked [:personal-profile :tab-key :connected-addins]])))}
                (loc/tr :settings/connected-plugins)])]]
           [:div
            {:class "md:col-span-8"}
            (case tab-key
              :my-details
              [details]

              :insider-groups
              [insider-groups]

              :security
              [:<>
               [security]
               (when (and (:tenant/has-sandbox? @session/tenant)
                          (not (:tenant/sandbox-disabled? @session/tenant)))
                 [sandbox])

               (when (or (session/sys-admin?)
                         (authentication/mfa-on? @session/tenant))
                 [auth/view])
               [sessions/view
                (:show-all-sessions? @db/state)
                (:tokens @db/state)
                (fn [_]
                  (load-tokens nil)
                  (swap! db/state assoc :show-all-sessions? true))]]
              :email-preferences
              [:<>
               [email-preferences]
               (when-not (session/is-read-only-user?)
                 [nylas-preferences])]
              :my-preferences
              [:<>
               [options]]

              :connected-addins
              [connected-plugins/content])]]]]))})))

(defn view
  [tab-key]
  [:<>
   [zap-toolbar/view db/state
    (partial zap-toolbar/back-button nil) nil []]
   [contents tab-key]])

(defmethod routing/render-view :personal-profile
  [{:keys [route-params]}]
  (let [tab-key (or (keyword (:tab-key route-params))
                    :my-details)]
    [default-page-layout/view
     {:class-name "zap-page personal-profile"}
     [:<>
      (when (string/ends-with? (:account/email @session/account) "@zapflow.com")
        (let [selected-theme @tailwind-theme/current-theme]
          [select/view
           {:on-change (fn [v]
                         (tailwind-theme/set-theme! v))
            :value     {:value selected-theme
                        :label selected-theme}
            :options   (->> ["zapflow" "zapflow_dark" "zapflow_hc" "teollisuussijoitus" "teollisuussijoitus_dark" "capman" "capman_dark"]
                            (mapv (fn [k]
                                    {:value k
                                     :label k})))}]))
      [view tab-key]]]))
